import React, { useState } from 'react';
import ArchV2Hasil from './ArchV2Hasil';

// ═══════════════════════════════════════════════════════════
//  PROMPT ARCHITECTURE V2 — tab Playground
//  Pilih produk + kategori + formula/style (atau preset) + bilangan scene + adapter video
//  + model OpenAI → Generate. Satu klik menjalankan SEMUA model yang ditanda (prompt dan
//  produk yang sama), setiap satu direkod berasingan dalam Test History.
//
//  Senarai model datang daripada server (senarai putih makmal "Ujian Master Prompt");
//  label mesra datang daripada LAB_MODELS di PipelineTestView.jsx — tiada senarai ketiga di sini.
// ═══════════════════════════════════════════════════════════

const KELAS_INPUT = 'w-full h-9 px-3 text-[14px] border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-slate-50 disabled:text-slate-400';
const KELAS_AREA = 'w-full px-3 py-2 text-[13px] border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-y';
const KELAS_LABEL = 'block text-[12px] text-slate-400 mb-1';
const TURUTAN_REASONING = ['none', 'minimal', 'low', 'medium', 'high', 'xhigh', 'max'];

const MEDAN_TAMBAHAN = [
  ['penerangan', 'Product description', 3, 'Apa produk ini dan bagaimana ia digunakan.'],
  ['ciriJualan', 'Selling points', 3, 'Satu baris satu sebab orang beli.'],
  ['spesifikasi', 'Specifications', 3, 'Saiz, isipadu, bahan, warna — fakta sahaja.'],
  ['sasaran', 'Target buyer', 1, 'Cth: pekerja pejabat yang bawa bekal.'],
  ['subkategori', 'Subcategory', 1, 'Cth: Food storage.'],
  ['nota', 'Creative notes (pilihan)', 2, 'Arahan kreatif untuk run ini. Tidak boleh mengatasi peraturan jujur.'],
];

/** Entri aktif + entri yang sedang dipilih walaupun dimatikan/diarkib (supaya pilihan tidak bertukar senyap). */
function pilihanBagi(senarai, idSemasa) {
  return (senarai || [])
    .filter((e) => (e.aktif && !e.diarkib) || e.id === idSemasa)
    .map((e) => ({ id: e.id, label: e.nama + (e.diarkib ? ' (archived)' : !e.aktif ? ' (disabled)' : '') }));
}

function Pilih({ label, nilai, onUbah, pilihan, kosong, disabled, bantu }) {
  return (
    <div>
      <label className={KELAS_LABEL}>{label}</label>
      <select value={nilai || ''} onChange={(e) => onUbah(e.target.value)} disabled={disabled} className={KELAS_INPUT}>
        {kosong != null && <option value="">{kosong}</option>}
        {pilihan.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
      {bantu && <p className="text-[11.5px] text-slate-400 leading-snug m-0 mt-1">{bantu}</p>}
    </div>
  );
}

export default function ArchV2Playground({
  lib, tetapan, ubah, tambahan, ubahTambahan, produkPustaka, onPilihProduk, labModels,
  reasoningBagi, gambar, onJana, onInspect, sedangJana, hasil, halangan, onNilaiDisimpan,
}) {
  const [butiranBuka, setButiranBuka] = useState(false);
  const p = lib.pustaka;
  const cari = (jenis, id) => p[jenis].find((e) => e.id === id) || null;

  const preset = tetapan.presetId ? cari('preset', tetapan.presetId) : null;
  const ceritaId = preset ? preset.ceritaId : tetapan.ceritaId;
  const gayaId = preset ? preset.gayaId : tetapan.gayaId;
  const adapter = cari('adapter', tetapan.adapterId);
  const tempohPilihan = adapter?.tempohDibenarkan || [];
  const manual = tetapan.sumberProduk === 'manual';

  const tukarAdapter = (id) => {
    const a = cari('adapter', id);
    const dibenarkan = a?.tempohDibenarkan || [];
    const lalai = cari('babak', lib.idTunggal)?.tempohLalai;
    const tempoh = dibenarkan.includes(Number(tetapan.tempoh))
      ? Number(tetapan.tempoh)
      : (dibenarkan.includes(lalai) ? lalai : dibenarkan[dibenarkan.length - 1]);
    ubah({ adapterId: id, tempoh });
  };

  const modelDipilih = tetapan.model || [];
  const senaraiModel = lib.model.map((m) => ({
    ...m,
    label: (labModels || []).find((l) => l.id === m.id)?.label || m.id,
  }));
  const togolModel = (id) => {
    const baru = modelDipilih.includes(id) ? modelDipilih.filter((x) => x !== id) : [...modelDipilih, id];
    ubah({ model: senaraiModel.map((m) => m.id).filter((x) => baru.includes(x)) });
  };
  const tahapSemua = new Set(senaraiModel.filter((m) => modelDipilih.includes(m.id)).flatMap((m) => m.reasoning));
  const pilihanReasoning = TURUTAN_REASONING.filter((t) => tahapSemua.has(t));

  return (
    <div>
      {/* ── Produk ── */}
      <div className="rounded-xl bg-white border border-slate-200 p-5 mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <p className="text-[14px] font-semibold text-slate-800 m-0">1 · Product</p>
          <div className="flex gap-1">
            {[['pustaka', 'Library product'], ['manual', 'Manual']].map(([id, label]) => (
              <button
                key={id}
                onClick={() => ubah({ sumberProduk: id })}
                className={`px-3 py-1.5 text-[12.5px] font-semibold rounded-lg ${tetapan.sumberProduk === id ? 'bg-indigo-50 text-indigo-600' : 'text-slate-500 hover:bg-slate-50'}`}
              >{label}</button>
            ))}
          </div>
        </div>

        {!manual && (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 overflow-hidden flex-shrink-0">
              {(produkPustaka?.imageBase64 || produkPustaka?.imageUrl)
                ? <img src={produkPustaka.imageBase64 || produkPustaka.imageUrl} alt="" className="w-full h-full object-cover" />
                : <span className="material-symbols-outlined text-[20px]">image</span>}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[14px] font-semibold text-slate-800 m-0 truncate">{produkPustaka?.name || 'Belum pilih produk'}</p>
              <p className="text-[12px] text-slate-400 m-0">
                {produkPustaka ? `dari library${produkPustaka.price ? ` · ${produkPustaka.price}` : ''}` : 'Pilih produk dari library (sama seperti tab lain makmal ini).'}
              </p>
            </div>
            {produkPustaka && (produkPustaka.hasData
              ? <span className="text-[11px] bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded font-semibold whitespace-nowrap">ada data</span>
              : <span className="text-[11px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded whitespace-nowrap">tiada data — isi butiran di bawah</span>)}
            <button onClick={onPilihProduk} className="h-9 flex items-center gap-2 px-3 text-[13px] font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 whitespace-nowrap">
              <span className="material-symbols-outlined text-[16px]">folder_open</span>{produkPustaka ? 'Tukar produk' : 'Pilih dari library'}
            </button>
          </div>
        )}

        {manual && (
          <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-3">
            <div>
              <label className={KELAS_LABEL}>Product name</label>
              <input value={tambahan.nama || ''} onChange={(e) => ubahTambahan({ nama: e.target.value })} maxLength={300} placeholder="Nama produk seperti di listing" className={KELAS_INPUT} />
            </div>
            <div>
              <label className={KELAS_LABEL}>Price (pilihan)</label>
              <input value={tambahan.harga || ''} onChange={(e) => ubahTambahan({ harga: e.target.value })} maxLength={60} placeholder="RM19.90" className={KELAS_INPUT} />
            </div>
          </div>
        )}

        {/* ── Vision: gambar produk dihantar terus kepada AI penulis prompt (satu call) ── */}
        <div className={`mt-3 rounded-lg border px-3 py-2.5 ${tetapan.vision ? 'border-indigo-200 bg-indigo-50/40' : 'border-slate-200'}`}>
          <div className="flex items-start gap-3">
            <button
              role="switch" aria-checked={!!tetapan.vision} onClick={() => ubah({ vision: !tetapan.vision })}
              className={`mt-0.5 w-9 h-5 rounded-full relative flex-shrink-0 transition-colors ${tetapan.vision ? 'bg-indigo-600' : 'bg-slate-300'}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${tetapan.vision ? 'left-[18px]' : 'left-0.5'}`} />
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-slate-800 m-0">Vision — send product photo to the AI</p>
              <p className="text-[11.5px] text-slate-500 leading-snug m-0 mt-0.5">
                AI tengok gambar sebenar + data produk dalam SATU call. Kalau data scrape bercanggah dengan gambar (cth bilangan bekas), AI ikut gambar dan tidak menambah bahagian yang tidak kelihatan.
                Kos gambar masuk dalam token input run (± 700–1,000 token).
              </p>
              {tetapan.vision && (manual
                ? <p className="text-[12px] text-amber-700 m-0 mt-1.5">Mod Manual tiada gambar — Vision hanya untuk produk dari library.</p>
                : gambar?.ralat
                  ? <p className="text-[12px] text-amber-700 m-0 mt-1.5">{gambar.ralat}</p>
                  : gambar?.url && <p className="text-[12px] text-emerald-700 m-0 mt-1.5">✓ Gambar produk di atas akan dihantar bersama prompt.</p>)}
            </div>
          </div>
        </div>

        <button onClick={() => setButiranBuka((v) => !v)} className="mt-3 text-[12px] font-semibold text-indigo-600 hover:underline">
          {butiranBuka || manual ? 'Butiran produk' : 'Tambah butiran produk / creative notes (pilihan)'}
        </button>
        {(butiranBuka || manual) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            {MEDAN_TAMBAHAN.map(([k, label, baris, bantu]) => (
              <div key={k}>
                <label className={KELAS_LABEL}>{label}</label>
                {baris > 1
                  ? <textarea value={tambahan[k] || ''} onChange={(e) => ubahTambahan({ [k]: e.target.value })} rows={baris} placeholder={bantu} className={KELAS_AREA} />
                  : <input value={tambahan[k] || ''} onChange={(e) => ubahTambahan({ [k]: e.target.value })} placeholder={bantu} className={KELAS_INPUT} />}
              </div>
            ))}
          </div>
        )}
        <p className="text-[11.5px] text-slate-400 m-0 mt-2">Data produk dihantar kepada AI sebagai FAKTA sahaja — AI diarah supaya tidak mereka spesifikasi, diskaun atau dakwaan yang tiada di sini.</p>
      </div>

      {/* ── Susunan kreatif ── */}
      <div className="rounded-xl bg-white border border-slate-200 p-5 mb-4">
        <p className="text-[14px] font-semibold text-slate-800 m-0 mb-3">2 · Creative setup</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Pilih
            label="Category" nilai={tetapan.kategoriId} onUbah={(v) => ubah({ kategoriId: v })}
            pilihan={pilihanBagi(p.kategori, tetapan.kategoriId)} bantu={cari('kategori', tetapan.kategoriId)?.penerangan}
          />
          <Pilih
            label="Creative Style Preset" nilai={tetapan.presetId} onUbah={(v) => ubah({ presetId: v })}
            kosong="(tiada — pilih formula + style sendiri)"
            pilihan={pilihanBagi(p.preset, tetapan.presetId)} bantu={preset?.penerangan}
          />
          <Pilih
            label={preset ? 'Story Formula (dari preset)' : 'Story Formula'} nilai={ceritaId} disabled={!!preset}
            onUbah={(v) => ubah({ ceritaId: v })}
            pilihan={pilihanBagi(p.cerita, ceritaId)} bantu={cari('cerita', ceritaId)?.penerangan}
          />
          <Pilih
            label={preset ? 'Presentation Style (dari preset)' : 'Presentation Style'} nilai={gayaId} disabled={!!preset}
            onUbah={(v) => ubah({ gayaId: v })}
            pilihan={pilihanBagi(p.gaya, gayaId)} bantu={cari('gaya', gayaId)?.penerangan}
          />
        </div>
        {preset?.videoPratonton && (
          <p className="text-[12px] m-0 mt-2"><a href={preset.videoPratonton} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">Lihat video pratonton preset ini ↗</a></p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_2fr] gap-3 mt-4 items-start">
          <div>
            <label className={KELAS_LABEL}>Scenes</label>
            <div className="flex gap-1">
              {Array.from({ length: lib.had.babakMaks - lib.had.babakMin + 1 }, (_, i) => lib.had.babakMin + i).map((n) => (
                <button
                  key={n}
                  onClick={() => ubah({ bilBabak: n })}
                  className={`w-9 h-9 rounded-lg text-[14px] font-bold border ${Number(tetapan.bilBabak) === n ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                >{n}</button>
              ))}
            </div>
          </div>
          <div>
            <label className={KELAS_LABEL}>Seconds per scene</label>
            <select value={String(tetapan.tempoh)} onChange={(e) => ubah({ tempoh: Number(e.target.value) })} className={KELAS_INPUT}>
              {!tempohPilihan.includes(Number(tetapan.tempoh)) && <option value={String(tetapan.tempoh)}>{tetapan.tempoh}s (tidak dibenarkan adapter)</option>}
              {tempohPilihan.map((t) => <option key={t} value={String(t)}>{t}s</option>)}
            </select>
          </div>
          <Pilih
            label="Video model adapter (model VIDEO sasaran — bukan model OpenAI)" nilai={tetapan.adapterId} onUbah={tukarAdapter}
            pilihan={pilihanBagi(p.adapter, tetapan.adapterId)}
            bantu={adapter ? `${adapter.modelVideo} · maks ${adapter.maksSyot} syot setiap scene` : null}
          />
        </div>
        <p className="text-[11.5px] text-slate-400 m-0 mt-2">Jumlah video ≈ {Number(tetapan.bilBabak) * Number(tetapan.tempoh)} saat ({tetapan.bilBabak} scene × {tetapan.tempoh}s).</p>
      </div>

      {/* ── Model OpenAI ── */}
      <div className="rounded-xl bg-white border border-slate-200 p-5 mb-4">
        <p className="text-[14px] font-semibold text-slate-800 m-0">3 · OpenAI model (penulis prompt)</p>
        <p className="text-[12px] text-slate-500 m-0 mt-0.5 mb-3">Senarai sama dengan tab "Ujian Master Prompt". Tanda lebih daripada satu untuk uji produk + prompt yang SAMA pada beberapa model serentak.</p>
        <div className="flex flex-wrap gap-2">
          {senaraiModel.map((m) => {
            const dipilih = modelDipilih.includes(m.id);
            return (
              <button
                key={m.id}
                onClick={() => togolModel(m.id)}
                className={`text-left rounded-lg border px-3 py-2 ${dipilih ? 'border-indigo-500 border-2 bg-indigo-50/40' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
              >
                <p className={`text-[13px] font-bold m-0 ${dipilih ? 'text-indigo-700' : 'text-slate-700'}`}>
                  <span className="material-symbols-outlined text-[15px] align-[-3px] mr-1">{dipilih ? 'check_box' : 'check_box_outline_blank'}</span>{m.label}
                </p>
                <p className="text-[11.5px] text-slate-400 m-0 mt-0.5">
                  {m.harga ? `$${m.harga.input} in / $${m.harga.output} out setiap 1M token` : 'tiada harga ditetapkan'}
                  {dipilih ? ` · reasoning → ${reasoningBagi(m.id)}` : ''}
                </p>
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
          <div>
            <label className={KELAS_LABEL}>Reasoning effort</label>
            <select value={tetapan.reasoning} onChange={(e) => ubah({ reasoning: e.target.value })} className={KELAS_INPUT}>
              {!pilihanReasoning.includes(tetapan.reasoning) && <option value={tetapan.reasoning}>{tetapan.reasoning}</option>}
              {pilihanReasoning.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className={KELAS_LABEL}>Verbosity</label>
            <select value={tetapan.verbosity} onChange={(e) => ubah({ verbosity: e.target.value })} className={KELAS_INPUT}>
              {lib.had.verbosity.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className={KELAS_LABEL}>Max output tokens</label>
            <input
              type="number" min={lib.had.tokenMin} max={lib.had.tokenMaks} step="500" value={tetapan.maxTokens}
              onChange={(e) => ubah({ maxTokens: e.target.value })} className={KELAS_INPUT}
            />
          </div>
        </div>
        <p className="text-[11.5px] text-slate-400 m-0 mt-2">Nama tahap reasoning berbeza ikut keluarga model (cth "minimal" lawan "none") — tahap yang benar-benar dihantar ditunjuk pada setiap kad model.</p>
      </div>

      {/* ── Butang ── */}
      <div className="flex items-center gap-3 flex-wrap mb-5">
        <button
          onClick={onJana}
          disabled={sedangJana || !!halangan}
          className="h-10 flex items-center gap-2 px-6 text-[14px] font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <span className={`material-symbols-outlined text-[18px] ${sedangJana ? 'animate-spin' : ''}`}>{sedangJana ? 'progressive_activity' : 'play_arrow'}</span>
          {sedangJana ? 'Menjana…' : `Generate${modelDipilih.length > 1 ? ` (${modelDipilih.length} model)` : ''}`}
        </button>
        <button onClick={onInspect} disabled={sedangJana} className="h-10 flex items-center gap-2 px-4 text-[13px] font-semibold text-slate-600 border border-slate-200 bg-white rounded-lg hover:bg-slate-50 disabled:opacity-40">
          <span className="material-symbols-outlined text-[16px]">manage_search</span>Inspect prompt dahulu (percuma)
        </button>
        {halangan && <span className="text-[12.5px] text-amber-700">{halangan}</span>}
        {!halangan && !sedangJana && <span className="text-[12px] text-slate-400">Setiap model = satu panggilan OpenAI berbayar (beberapa sen). Tiada kuota pelanggan disentuh.</span>}
      </div>

      {/* ── Hasil ── */}
      {hasil.length > 0 && (
        <div className={`grid grid-cols-1 gap-3 ${hasil.length === 2 ? 'lg:grid-cols-2' : hasil.length >= 3 ? 'xl:grid-cols-3' : ''}`}>
          {hasil.map((r, i) => <ArchV2Hasil key={r.id || r._model || i} run={r} kriteria={lib.kriteria} onNilaiDisimpan={onNilaiDisimpan} />)}
        </div>
      )}
    </div>
  );
}
